import {
    type View,
    Platform
} from "react-native";
import type {
    MessageScrollerNavigateConstantType,
    MessageScrollerEngineConstantType,
    MessageScrollerEngineResultType,
    MessageScrollerHighlightOptions,
    MessageScrollerScrollMetrics,
    MessageScrollerFocusableType,
    MessageScrollerScrollBoxType,
    MessageScrollerItemRecord,
    MessageScrollerItemMeta,
    MessageScrollerBoxType
} from "./type";

const MESSAGE_SCROLLER_PREVIOUS_KEYS = [
    "ArrowUp",
    "PageUp"
];

const MESSAGE_SCROLLER_NEXT_KEYS = [
    "ArrowDown",
    "PageDown"
];

const MESSAGE_SCROLLER_PENDING_ATTEMPTS = 6;

const isFocusable = (value: unknown): value is MessageScrollerFocusableType => {
    return typeof value === "object" && value !== null && "focus" in value && typeof value.focus === "function";
};

const hasQuery = (value: unknown): value is Required<Pick<MessageScrollerFocusableType, "querySelector">> => {
    return typeof value === "object" && value !== null && "querySelector" in value && typeof value.querySelector === "function";
};

const hasBox = (value: unknown): value is MessageScrollerBoxType => {
    return typeof value === "object" && value !== null && "offsetTop" in value && typeof value.offsetTop === "number" && "offsetHeight" in value && typeof value.offsetHeight === "number";
};

const hasScrollBox = (value: unknown): value is MessageScrollerScrollBoxType => {
    return typeof value === "object" && value !== null && "scrollTop" in value && typeof value.scrollTop === "number" && "scrollHeight" in value && typeof value.scrollHeight === "number" && "clientHeight" in value && typeof value.clientHeight === "number";
};

const focusNode = (node: View | null) => {
    if(!node) {
        return;
    }

    const article = hasQuery(node) ? node.querySelector("article") : null;

    const target = isFocusable(article) ? article : node;

    if(isFocusable(target)) {
        target.focus();
    }
};

export const createMessageScrollerEngine = ({
    viewportRef,
    getConfig,
    store
}: MessageScrollerEngineConstantType): MessageScrollerEngineResultType => {
    const items = new Map<string, MessageScrollerItemRecord>();

    const known = new Set<string>();

    const timers = new Set<ReturnType<typeof setTimeout>>();

    const metrics: MessageScrollerScrollMetrics = {
        viewportHeight: 0,
        contentHeight: 0,
        offset: 0
    };

    let anchorSnapshot: |
    {
        delta: number;
        id: string;
    } |
    null = null;

    let pendingScroll: |
    {
        attempts: number;
        y: number;
    } |
    null = null;

    let frame: number | null = null;

    let pinnedAnchorId: string | null = null;

    let programmaticUntil = 0;

    let pinnedUntil = 0;

    let isLoadingOlder = false;

    let isInitialized = false;

    let isDestroyed = false;

    let isFollowing = true;

    let orderCounter = 0;

    let lastOffset = 0;

    const getSortedItems = () => {
        return Array.from(items.entries()).map(([
            id,
            record
        ]) => ({
            ...record,
            id
        })).sort((first, second) => first.y - second.y || first.order - second.order);
    };

    const getItemsBottom = () => {
        let bottom = 0;

        items.forEach((record) => {
            bottom = Math.max(bottom, record.y + Math.max(record.height, 0));
        });

        return bottom;
    };

    const getIsMeasuring = () => {
        return Array.from(items.values()).some((record) => record.height < 0);
    };

    const refreshMetrics = () => {
        const node = viewportRef.current;

        if(Platform.OS !== "web" || !hasScrollBox(node)) {
            return;
        }

        metrics.viewportHeight = node.clientHeight;

        metrics.contentHeight = node.scrollHeight;

        metrics.offset = node.scrollTop;
    };

    const refreshItems = () => {
        if(Platform.OS !== "web") {
            return;
        }

        items.forEach((record) => {
            if(!hasBox(record.node)) {
                return;
            }

            record.height = record.node.offsetHeight;

            record.y = record.node.offsetTop;
        });
    };

    const addTimer = (callback: () => void, delay: number) => {
        const timer = setTimeout(() => {
            timers.delete(timer);

            if(!isDestroyed) {
                callback();
            }
        }, delay);

        timers.add(timer);
    };

    const scrollTo = (y: number, isAnimated: boolean) => {
        const {
            durations
        } = getConfig();

        const currentY = Math.max(0, y);

        programmaticUntil = Date.now() + (isAnimated ? durations.long + durations.short : durations.short);

        if(!isAnimated) {
            metrics.offset = currentY;
        }

        lastOffset = isAnimated ? lastOffset : currentY;

        viewportRef.current?.scrollTo({
            animated: isAnimated,
            y: currentY
        });
    };

    const updateEdges = () => {
        const {
            padding
        } = getConfig();

        const maxOffset = Math.max(0, metrics.contentHeight - metrics.viewportHeight);

        const isAtEnd = maxOffset - metrics.offset <= padding || Date.now() < pinnedUntil;

        const isAtStart = metrics.offset <= padding;

        store.update({
            ...(isAtEnd ? {
                newMessageCount: 0
            } : {
            }),
            isFadeStart: !isAtStart,
            isFadeEnd: !isAtEnd,
            isMeasured: true,
            isAtStart,
            isAtEnd
        });
    };

    const updateAnchor = () => {
        const {
            peek
        } = getConfig();

        const anchors = getSortedItems().filter((record) => record.isScrollAnchor && record.y <= metrics.offset + peek + 1);

        store.update({
            currentAnchorId: anchors[anchors.length - 1]?.id ?? null
        });
    };

    const updateSnapshot = () => {
        const first = getSortedItems().find((record) => record.height >= 0 && record.y + record.height > metrics.offset);

        anchorSnapshot = first ? {
            delta: first.y - metrics.offset,
            id: first.id
        } : null;
    };

    const updateSpacer = () => {
        const {
            stackBehaviour,
            padding,
            peek
        } = getConfig();

        const anchor = pinnedAnchorId ? items.get(pinnedAnchorId) : undefined;

        if(!anchor || stackBehaviour === "bottom") {
            store.update({
                spacerHeight: 0
            });

            return;
        }

        const tail = getItemsBottom() - anchor.y + padding;

        store.update({
            spacerHeight: Math.round(Math.max(0, metrics.viewportHeight - peek - tail))
        });
    };

    const loadOlder = () => {
        const {
            onLoadOlder
        } = getConfig();

        if(!onLoadOlder || isLoadingOlder) {
            return;
        }

        isLoadingOlder = true;

        store.update({
            isLoadingOlder: true
        });

        Promise.resolve(onLoadOlder()).catch(() => undefined).finally(() => {
            isLoadingOlder = false;

            if(!isDestroyed) {
                store.update({
                    isLoadingOlder: false
                });
            }
        });
    };

    const initialize = () => {
        const config = getConfig();

        isInitialized = true;

        items.forEach((_, id) => {
            known.add(id);
        });

        const sorted = getSortedItems();

        let position = config.defaultScrollPosition;

        if(position === "first-unread") {
            const unread = sorted.find((record) => record.isUnread);

            if(unread) {
                isFollowing = false;

                scrollTo(unread.y - config.peek, false);

                return;
            }

            position = "last-anchor";
        }

        if(position === "last-anchor") {
            const anchors = sorted.filter((record) => record.isScrollAnchor);

            const last = anchors[anchors.length - 1];

            if(last && getItemsBottom() - last.y + config.padding > metrics.viewportHeight) {
                isFollowing = false;

                scrollTo(last.y - config.peek, false);

                return;
            }

            position = "end";
        }

        if(position === "start") {
            isFollowing = false;

            scrollTo(0, false);

            return;
        }

        isFollowing = config.isAutoScroll;

        scrollTo(Math.max(0, metrics.contentHeight - metrics.viewportHeight), false);
    };

    const handleAppend = (records: Array<MessageScrollerItemRecord & {
        id: string;
    }>) => {
        const config = getConfig();

        const anchor = [...records].reverse().find((record) => record.isScrollAnchor);

        if(config.stackBehaviour === "top" && anchor && config.isAutoScroll) {
            pinnedAnchorId = anchor.id;

            isFollowing = true;

            updateSpacer();

            pendingScroll = {
                y: anchor.y - config.peek,
                attempts: 0
            };

            store.update({
                newMessageCount: 0
            });

            return;
        }

        if(isFollowing && config.isAutoScroll) {
            return;
        }

        store.update({
            newMessageCount: store.getSnapshot().newMessageCount + records.length
        });
    };

    const handleNewItems = () => {
        known.forEach((id) => {
            if(!items.has(id)) {
                known.delete(id);
            }
        });

        const records = getSortedItems().filter((record) => !known.has(record.id));

        if(!records.length) {
            return;
        }

        const knownRecords = getSortedItems().filter((record) => known.has(record.id));

        const minKnownY = knownRecords[0]?.y ?? 0;

        const isPrepend = knownRecords.length > 0 && records.every((record) => record.y < minKnownY);

        records.forEach((record) => {
            known.add(record.id);
        });

        if(isPrepend) {
            const target = anchorSnapshot ? items.get(anchorSnapshot.id) : undefined;

            if(getConfig().isPreserveScrollOnPrepend && anchorSnapshot && target) {
                scrollTo(target.y - anchorSnapshot.delta, false);
            }

            return;
        }

        handleAppend(records);
    };

    const applyPendingScroll = () => {
        if(!pendingScroll) {
            return false;
        }

        const {
            isReduced
        } = getConfig();

        const range = metrics.contentHeight - metrics.viewportHeight;

        pendingScroll.attempts += 1;

        if(range >= pendingScroll.y - 1 || pendingScroll.attempts > MESSAGE_SCROLLER_PENDING_ATTEMPTS) {
            scrollTo(pendingScroll.y, !isReduced);

            pinnedUntil = programmaticUntil;

            pendingScroll = null;

            return false;
        }

        schedule();

        return true;
    };

    const applyFollow = () => {
        const {
            isAutoScroll
        } = getConfig();

        if(!isFollowing || !isAutoScroll || store.getSnapshot().spacerHeight > 0) {
            return;
        }

        const target = Math.max(0, metrics.contentHeight - metrics.viewportHeight);

        if(target - metrics.offset > 1) {
            scrollTo(target, false);
        }
    };

    const settle = () => {
        if(isDestroyed) {
            return;
        }

        refreshMetrics();

        if(metrics.viewportHeight > 0 && !items.size) {
            updateEdges();

            return;
        }

        if(!metrics.viewportHeight || !metrics.contentHeight || !items.size) {
            return;
        }

        refreshItems();

        if(getIsMeasuring()) {
            return;
        }

        if(!isInitialized) {
            initialize();
        } else {
            handleNewItems();
        }

        updateSpacer();

        if(applyPendingScroll()) {
            return;
        }

        applyFollow();

        updateEdges();

        updateAnchor();

        updateSnapshot();
    };

    function schedule() {
        if(frame !== null || isDestroyed) {
            return;
        }

        frame = requestAnimationFrame(() => {
            frame = null;

            settle();
        });
    }

    const handleScroll = ({
        viewportHeight,
        contentHeight,
        offset
    }: MessageScrollerScrollMetrics) => {
        const config = getConfig();

        const isProgrammatic = Date.now() < programmaticUntil;

        metrics.contentHeight = contentHeight;

        metrics.viewportHeight = viewportHeight;

        metrics.offset = offset;

        if(!isProgrammatic && offset < lastOffset - 1) {
            isFollowing = false;
        }

        lastOffset = offset;

        updateEdges();

        if(!isProgrammatic && store.getSnapshot().isAtEnd) {
            isFollowing = config.isAutoScroll;
        }

        updateAnchor();

        updateSnapshot();

        if(isInitialized && config.hasOlderMessages && offset <= config.peek) {
            loadOlder();
        }
    };

    const handleContentSizeChange = (height: number) => {
        metrics.contentHeight = height;

        schedule();
    };

    const handleViewportLayout = (height: number) => {
        metrics.viewportHeight = height;

        schedule();
    };

    const handleItemLayout = (id: string, y: number, height: number) => {
        const record = items.get(id);

        if(!record) {
            return;
        }

        record.height = height;

        record.y = y;

        schedule();
    };

    const registerItem = (id: string, node: View | null, meta: MessageScrollerItemMeta) => {
        const record = items.get(id);

        if(record) {
            record.isScrollAnchor = meta.isScrollAnchor;

            record.isUnread = meta.isUnread;

            record.node = node;
        } else {
            items.set(id, {
                isScrollAnchor: meta.isScrollAnchor,
                isUnread: meta.isUnread,
                order: orderCounter,
                height: -1,
                node,
                y: 0
            });

            orderCounter += 1;
        }

        schedule();

        return () => {
            items.delete(id);

            known.delete(id);

            if(pinnedAnchorId === id) {
                pinnedAnchorId = null;
            }

            if(!items.size) {
                isInitialized = false;

                pendingScroll = null;

                anchorSnapshot = null;
            }

            schedule();
        };
    };

    const handleNavigate = ({
        key,
        id
    }: MessageScrollerNavigateConstantType) => {
        refreshItems();

        const sorted = getSortedItems();

        const index = sorted.findIndex((record) => record.id === id);

        if(index < 0) {
            return false;
        }

        if(MESSAGE_SCROLLER_PREVIOUS_KEYS.includes(key)) {
            if(index === 0) {
                loadOlder();

                return true;
            }

            focusNode(sorted[index - 1]?.node ?? null);

            return true;
        }

        if(MESSAGE_SCROLLER_NEXT_KEYS.includes(key)) {
            focusNode(sorted[Math.min(index + 1, sorted.length - 1)]?.node ?? null);

            return true;
        }

        if(key === "Home") {
            focusNode(sorted[0]?.node ?? null);

            return true;
        }

        if(key === "End") {
            focusNode(sorted[sorted.length - 1]?.node ?? null);

            return true;
        }

        return false;
    };

    const handleFocusFirstVisible = () => {
        refreshMetrics();

        refreshItems();

        const sortedItems = getSortedItems().filter((record) => record.height >= 0);

        const first = sortedItems.find((record) => record.y >= metrics.offset) ?? sortedItems.find((record) => record.y + record.height > metrics.offset);

        focusNode(first?.node ?? null);
    };

    const handleFollowRelease = () => {
        isFollowing = false;

        pinnedUntil = 0;
    };

    const scrollToMessage = (id: string, options?: MessageScrollerHighlightOptions) => {
        const record = items.get(id);

        if(!record) {
            return false;
        }

        const config = getConfig();

        refreshItems();

        isFollowing = false;

        pendingScroll = null;

        pinnedUntil = 0;

        scrollTo(record.y - config.peek, !config.isReduced);

        if(options?.isHighlighted) {
            store.update({
                highlightedId: id
            });

            addTimer(() => {
                store.update({
                    highlightedId: null
                });
            }, config.durations.breathe + config.durations.long);
        }

        addTimer(() => {
            focusNode(items.get(id)?.node ?? null);
        }, config.durations.short);

        return true;
    };

    const scrollToStart = () => {
        const config = getConfig();

        isFollowing = false;

        pendingScroll = null;

        pinnedUntil = 0;

        scrollTo(0, !config.isReduced);
    };

    const scrollToEnd = () => {
        const config = getConfig();

        pinnedAnchorId = null;

        pendingScroll = null;

        pinnedUntil = 0;

        isFollowing = config.isAutoScroll;

        programmaticUntil = Date.now() + config.durations.long + config.durations.short;

        store.update({
            newMessageCount: 0,
            spacerHeight: 0
        });

        viewportRef.current?.scrollToEnd({
            animated: !config.isReduced
        });
    };

    const destroy = () => {
        isDestroyed = true;

        if(frame !== null) {
            cancelAnimationFrame(frame);
        }

        timers.forEach((timer) => {
            clearTimeout(timer);
        });

        timers.clear();
    };

    const revive = () => {
        isDestroyed = false;
    };

    return {
        engine: {
            getIsInitialized: () => isInitialized,
            handleContentSizeChange,
            handleFocusFirstVisible,
            handleViewportLayout,
            handleFollowRelease,
            handleItemLayout,
            handleNavigate,
            handleScroll,
            registerItem,
            viewportRef
        },
        commands: {
            scrollToMessage,
            scrollToStart,
            scrollToEnd
        },
        destroy,
        revive
    };
};
